import React, { useEffect, useState } from 'react';
import { IconButton, Skeleton } from '@mui/material';
import { useAtomValue } from 'jotai';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import {
  allOutcomesAtom,
  selectedBranchAtom,
  userBranchesAtom,
} from '../../../Atoms';
import useAPI from '../../../api';
import useTranslate from '../../../Hooks/useTranslate';
import { culture, formatPayloadDate } from '../../../Helpers/dates';
import { CenteredFlexBox, ColumnFlexBox } from '../../FlexBox';
import MobileHeader from '../MobileHeader';
import ReportFilterSheet from '../ReportFilterSheet';
import ReportListSheet from '../ReportListSheet';
import SegmentedControl from '../SegmentedControl';
import useMobileNavigation from '../useMobileNavigation';
import {
  FILTER_CONFIG,
  SCOPE_REPORTS,
  BASE_FILTERS,
  sectionForReport,
  reportForSection,
} from './reportViews/reportConfig';
import { monthRange } from './reportViews/reportUtils';
import MobileCallsReport from './reportViews/MobileCallsReport';
import MobileInboundReport from './reportViews/MobileInboundReport';
import MobileRedemptionReport from './reportViews/MobileRedemptionReport';
import MobileRollingReport from './reportViews/MobileRollingReport';
import MobileWaveReport from './reportViews/MobileWaveReport';

/**
 * MobileReport — the Report tab (mockup screen 9). A slim orchestrator: it owns
 * the report selection, the filter draft/apply flow and all data fetching, then
 * hands each report its raw response to a dedicated view in `reportViews/`.
 * See MOBILE_PLAN.md §F(9).
 */
const MobileReport = function MobileReport() {
  const translate = useTranslate();
  const API = useAPI();
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const userBranches = useAtomValue(userBranchesAtom) || [];
  const allOutcomes = useAtomValue(allOutcomesAtom);
  const branchId = selectedBranch?.id;

  // The reports the user holds on desktop; the tab is only shown when there is one.
  const { reports } = useMobileNavigation();

  // Restore the last-viewed report from the persisted desktop section key, if it is still one the
  // user holds.
  const [report, setReport] = useState(() => {
    const saved = reportForSection(sessionStorage.getItem('currentSection'));
    return reports.some(({ key }) => key === saved)
      ? saved
      : (reports[0]?.key ?? 'CALLS');
  });
  const [reportSheetOpen, setReportSheetOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // Persist the active report as the desktop section key so a reload lands back
  // on the Report tab with the same report selected (same key desktop uses).
  useEffect(() => {
    sessionStorage.setItem('currentSection', sectionForReport(report));
  }, [report]);

  // `filters` is what the reports actually query; `draft` is the in-progress
  // edit inside the sheet, committed to `filters` only on Confirm.
  const [filters, setFilters] = useState(BASE_FILTERS);
  const [draft, setDraft] = useState(BASE_FILTERS);
  const { monthOffset, grouping, branches, sections, operator } = filters;

  // Branch/User scope is an always-visible control (not in the sheet), applied
  // on change.
  const [scope, setScope] = useState('BRANCH');

  const [sectionOptions, setSectionOptions] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);

  const [callPerf, setCallPerf] = useState(null);
  const [inbound, setInbound] = useState(null);
  const [redemption, setRedemption] = useState(null);
  const [rolling, setRolling] = useState(null);
  const [wave, setWave] = useState(null);

  const branchOptions = userBranches.map((b) => ({
    value: b.id,
    label: b.branchDescription,
  }));

  // Default the branch filter to the globally-selected branch.
  useEffect(() => {
    if (branchId)
      setFilters((f) =>
        f.branches.length ? f : { ...f, branches: [branchId] }
      );
  }, [branchId]);

  // Section options (required by getCallPerformance); default to all.
  useEffect(() => {
    API.getWaveSections(
      (res) => {
        const list = res || [];
        setSectionOptions(list.map((s) => ({ value: s.id, label: s.name })));
        setFilters((f) => ({ ...f, sections: list.map((s) => s.id) }));
      },
      () => setSectionOptions([])
    );
  }, []);

  // Operator options (single-select, 0 = all — the desktop NO_SELECTION).
  useEffect(() => {
    API.getUsersByRoles(
      ['OPERATOR', 'SUPERVISOR'],
      (res) =>
        setOperatorOptions([
          { value: 0, label: translate('ALL') },
          ...(res || [])
            .filter((u) => !u.deletedOn)
            .map((u) => ({ value: u.id, label: `${u.name} ${u.surname}` })),
        ]),
      () => setOperatorOptions([{ value: 0, label: translate('ALL') }])
    );
  }, []);

  useEffect(() => {
    const [from, to] = monthRange(monthOffset);
    const start = formatPayloadDate(from)?.toISOString();
    const end = formatPayloadDate(to)?.toISOString();
    const silent = { silent: true };
    const branchIds = branches.length ? branches : branchId ? [branchId] : [];

    if (report === 'CALLS') {
      setCallPerf(null);
      if (!branchIds.length || !sections.length) {
        setCallPerf({});
        return;
      }
      API.getCallPerformance(
        start,
        end,
        grouping,
        { branchIds, operatorId: operator || null, sections },
        silent,
        (res) => setCallPerf(res || {}),
        () => setCallPerf({})
      );
    } else if (report === 'INBOUND') {
      setInbound(null);
      if (!branchIds.length) {
        setInbound({});
        return;
      }
      API.getPivotData(
        start,
        end,
        grouping,
        { direction: 1, branchIds },
        silent,
        (res) => setInbound(res || {}),
        () => setInbound({})
      );
    } else if (report === 'REDEMPTION') {
      setRedemption(null);
      API.getRedemption(
        start,
        end,
        scope,
        silent,
        (res) => setRedemption(res?.redemptionData || []),
        () => setRedemption([])
      );
    } else if (report === 'ROLLING') {
      setRolling(null);
      API.getRollingRedemption(
        formatPayloadDate(new Date())?.toISOString(),
        scope,
        silent,
        (res) => setRolling(res?.redemptionData || []),
        () => setRolling([])
      );
    } else if (report === 'WAVE') {
      setWave(null);
      const wavePeriod = `${from.getFullYear()}${String(
        from.getMonth() + 1
      ).padStart(2, '0')}`;
      const waveBranch = branchIds.length === 1 ? branchIds[0] : null;
      API.getWaveReport(
        wavePeriod,
        waveBranch,
        (res) => setWave(res?.graphData || {}),
        () => setWave({})
      );
    }
  }, [
    report,
    monthOffset,
    grouping,
    scope,
    branches,
    sections,
    operator,
    branchId,
  ]);

  const renderSkeleton = () => (
    <>
      <CenteredFlexBox sx={{ gap: 1.25 }}>
        <Skeleton
          variant="rounded"
          height={80}
          sx={{ flex: 1, borderRadius: '16px' }}
        />
        <Skeleton
          variant="rounded"
          height={80}
          sx={{ flex: 1, borderRadius: '16px' }}
        />
      </CenteredFlexBox>
      <Skeleton variant="rounded" height={168} sx={{ borderRadius: '18px' }} />
      <Skeleton variant="rounded" height={168} sx={{ borderRadius: '18px' }} />
    </>
  );

  const loadingByReport = {
    CALLS: callPerf === null,
    INBOUND: inbound === null,
    REDEMPTION: redemption === null,
    ROLLING: rolling === null,
    WAVE: wave === null,
  };

  const renderSegment = () => {
    if (loadingByReport[report]) return renderSkeleton();
    if (report === 'CALLS')
      return (
        <MobileCallsReport callPerf={callPerf} allOutcomes={allOutcomes} />
      );
    if (report === 'INBOUND')
      return (
        <MobileInboundReport inbound={inbound} allOutcomes={allOutcomes} />
      );
    if (report === 'REDEMPTION')
      return <MobileRedemptionReport redemption={redemption} />;
    if (report === 'ROLLING') return <MobileRollingReport rolling={rolling} />;
    return <MobileWaveReport graphData={wave} />;
  };

  const current = reports.find((r) => r.key === report) || reports[0];
  const filterConfig = FILTER_CONFIG[report] || {};
  const hasSheetFilters = Object.values(filterConfig).some(Boolean);
  const showScopeControl = SCOPE_REPORTS.includes(report);

  return (
    <>
      <MobileHeader
        title={translate(current.labelKey)}
        onTitleClick={() => setReportSheetOpen(true)}
        avatar={
          hasSheetFilters ? (
            <IconButton
              aria-label={translate('FILTERS')}
              onClick={() => {
                setDraft(filters);
                setFilterOpen(true);
              }}
              sx={{
                flex: 'none',
                width: 46,
                height: 46,
                borderRadius: '14px',
                bgcolor: 'background.default',
              }}
            >
              <FilterListRoundedIcon />
            </IconButton>
          ) : null
        }
        extra={
          showScopeControl ? (
            <SegmentedControl
              value={scope}
              onChange={setScope}
              options={[
                { value: 'BRANCH', label: translate('BRANCH') },
                { value: 'USER', label: translate('USER') },
              ]}
            />
          ) : undefined
        }
      />
      <ColumnFlexBox sx={{ p: 2, gap: 1.75 }}>{renderSegment()}</ColumnFlexBox>

      <ReportListSheet
        open={reportSheetOpen}
        onClose={() => setReportSheetOpen(false)}
        title={translate('REPORT_TYPE')}
        items={reports.map((item) => ({
          ...item,
          label: translate(item.labelKey),
        }))}
        value={report}
        onChange={({ key }) => setReport(key)}
      />

      <ReportFilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        {...filterConfig}
        monthLabel={monthRange(draft.monthOffset)[0].toLocaleDateString(
          culture,
          { month: 'long', year: 'numeric' }
        )}
        onMonthChange={(delta) =>
          setDraft((d) => ({
            ...d,
            monthOffset: Math.min(0, d.monthOffset + delta),
          }))
        }
        canGoNext={draft.monthOffset < 0}
        grouping={draft.grouping}
        onGroupingChange={(value) =>
          setDraft((d) => ({ ...d, grouping: value }))
        }
        branchOptions={branchOptions}
        branches={draft.branches}
        onBranchesChange={(value) =>
          setDraft((d) => ({ ...d, branches: value }))
        }
        sectionOptions={sectionOptions}
        sections={draft.sections}
        onSectionsChange={(value) =>
          setDraft((d) => ({ ...d, sections: value }))
        }
        operatorOptions={operatorOptions}
        operator={draft.operator}
        onOperatorChange={(value) =>
          setDraft((d) => ({ ...d, operator: value }))
        }
        onConfirm={() => {
          setFilters(draft);
          setFilterOpen(false);
        }}
      />
    </>
  );
};

export default MobileReport;
